// Considered: TickSliderAtom (an input, not a display), PeriodGridMolecule (a calendar grid) —
// neither is a five-slot hit/miss/due strip with per-tick inspection. See spec Reuse Audit
// (specs/stream-trajectory-tools.md § New components — justified).
//
// Source: design/metaspace-tools/metaspace-trajectory-v2.jsx:167-186. One row PER aspect — each
// track carries its own tempo; rows are never merged across aspects.

import React from 'react';
import { useTranslation } from 'localization';
import InspectableMolecule from '../Inspectable/InspectableMolecule';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import type { TrajectoryCadenceEntry } from 'types/trajectory';
import type { InspectorPayload } from 'types/inspector';
import './CadenceRowMolecule.scss';

export interface CadenceRowMoleculeProps {
  /** Chronologically-ordered periods for THIS aspect's own cadence — never merged with another aspect. */
  entries: TrajectoryCadenceEntry[];
  buildPayload: (entry: TrajectoryCadenceEntry) => InspectorPayload;
  onInspect?: (payload: InspectorPayload) => void;
  className?: string;
}

function tickIcon(state: TrajectoryCadenceEntry['state']): string {
  return state === 'hit' ? 'lucide:check' : 'lucide:x';
}

function weekdayShort(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { weekday: 'short' });
}

const CadenceRowMolecule: React.FC<CadenceRowMoleculeProps> = ({ entries, buildPayload, onInspect, className }) => {
  const { t } = useTranslation();

  return (
    <div className={['cadence-row-molecule', className].filter(Boolean).join(' ')}>
      {entries.map((entry) => {
        const payload = buildPayload(entry);
        const tickClasses = [
          'cadence-row-molecule__tick',
          `cadence-row-molecule__tick--${entry.state}`,
        ].join(' ');

        if (entry.state === 'due') {
          return (
            <span key={entry.periodId} className="cadence-row-molecule__slot cadence-row-molecule__slot--due">
              <InspectableMolecule
                payload={payload}
                onInspect={onInspect}
                peek={t('trajectory.cadence.duePeek', { defaultValue: 'Window closes soon — submit here' })}
                aria-label={t('trajectory.cadence.dueAriaLabel', { defaultValue: 'Check-in due' })}
              >
                <span className="cadence-row-molecule__due-ring" />
                <span className="cadence-row-molecule__due-label">{weekdayShort(entry.window.to)}</span>
              </InspectableMolecule>
            </span>
          );
        }

        if (entry.state === 'upcoming') {
          return (
            <span key={entry.periodId} className="cadence-row-molecule__slot">
              <span
                className="cadence-row-molecule__tick cadence-row-molecule__tick--upcoming"
                role="img"
                aria-label={t('trajectory.cadence.upcomingAriaLabel', { defaultValue: 'Upcoming' })}
              />
            </span>
          );
        }

        return (
          <span key={entry.periodId} className="cadence-row-molecule__slot">
            <InspectableMolecule
              payload={payload}
              onInspect={onInspect}
              peek={entry.state === 'hit'
                ? t('trajectory.cadence.hitPeek', { name: entry.checkedInBy?.name, defaultValue: `${entry.checkedInBy?.name ?? ''} checked in` })
                : t('trajectory.cadence.missPeek', { defaultValue: 'Missed — degradation applied, check in late' })}
              aria-label={entry.state}
            >
              <span className={tickClasses}>
                <GlyphAtom glyph={tickIcon(entry.state)} />
              </span>
            </InspectableMolecule>
          </span>
        );
      })}
    </div>
  );
};

CadenceRowMolecule.displayName = 'CadenceRowMolecule';

export default CadenceRowMolecule;
